import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import CollectionGrid from './components/CollectionGrid';
import ProductGrid from './components/ProductGrid';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import Cart from './components/Cart';
import LoadingScreen from './components/LoadingScreen';
import SEO from './components/SEO';
import OrganizationSchema from './components/OrganizationSchema';
import { useStoreData } from './hooks/useStoreData';
import Checkout from './pages/Checkout';
import CategoryPage from './pages/CategoryPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import FAQ from './pages/FAQ';
import AboutUs from './pages/AboutUs';
import OurStory from './pages/OurStory';
import ExchangePolicy from './pages/ExchangePolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider, useCart } from './contexts/CartContext';

function HomePage() {
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { loading, products, categories, settings } = useStoreData();

  // Show loading screen while data is being fetched or if critical data is missing
  const isDataReady = !loading && products.length > 0 && categories.length > 0 && settings;

  // Add a small delay before showing content to ensure smooth transition
  React.useEffect(() => {
    if (isDataReady) {
      const timer = setTimeout(() => setShowContent(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isDataReady]);

  if (!showContent) {
    return <LoadingScreen />;
  }

  return (
    <div className="App animate-fade-in">
      <SEO
        title="Rizzahra | Premium Abayas & Modern Islamic Fashion"
        description="Shop elegant modern abayas at Rizzahra. Premium quality Islamic clothing with contemporary designs. Modest fashion for the modern woman since 2025."
        keywords="abayas, modern abayas, islamic clothing, modest fashion, premium abayas, hijab, muslim clothing, contemporary abayas"
        url="/"
      />
      <OrganizationSchema />
      <Header
        cartCount={getCartCount()}
        onCartClick={() => setIsCartOpen(true)}
        onAdminClick={() => navigate('/admin')}
      />
      <HeroSection />
      <ProductGrid />
      <Testimonials />
      <Footer />
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </div>
  );
}

function AdminRoute() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  if (!currentUser) {
    return <AdminLogin onLoginSuccess={() => {}} />;
  }

  return <AdminDashboard onBackToStore={() => navigate('/')} />;
}

function AppContent() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/category/:category" element={<CategoryPage />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/admin" element={<AdminRoute />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/our-story" element={<OurStory />} />
      <Route path="/exchange-policy" element={<ExchangePolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;